import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router';
import cookie from 'react-cookie';
import { Query } from 'react-apollo';
import WithAuthService from '@/hocs/withAuthService';

import { EventSummary } from '../../state/services/gql/event';

import getLock from '../../state/services/auth/getLock';
import {
  trackPageVisit,
  trackLoginWallViewed,
  PAGE_NAME,
} from '../../utils/tracking';
import { routerContextShape } from '../../proptypes';
import {
  USER_DISPLAY_NAME_COOKIE_NAME,
  USER_COOKIE_NAME,
} from '../../state/services/auth';

import EventLogo from '../EventLogo';
import Logo from '../Logo';
import styles, { // eslint-disable-line no-unused-vars
  blurb as blurbStyle,
  container,
  containerTransparent as containerTransparentStyle,
  heading as headingStyle,
  layout,
  layoutTransparent as layoutTransparentStyle,
  logo,
  personalisedHeadingStyle,
  reviewHeader,
  reviewLogo,
  reviewOrganiser,
  reviewTitle,
  shortBlurb,
  stravaUser,
  terms,
} from './styles.module.css';

const lockContainerId = 'lock';
const secondLockContainerId = 'lock2';

export const CONNECTIONS = {
  GOOGLE: 'google-oauth2',
  FACEBOOK: 'facebook',
  STRAVA: 'strava',
};
/**
 * Login container component.
 * Composites page and renders Auth0 Lock component.
 * Wrapped in router props to get return URL to configure lock.
 */
class Login extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      heading: props.heading,
      blurb: props.blurb,
      displayName: undefined,
      allowedConnections: undefined,
      forceStrava: false,
      showSecondLock: false,
    };
  }

  async componentDidMount() {
    await this.configureLock(lockContainerId);
    trackPageVisit(PAGE_NAME.LOGIN_PAGE);

    trackLoginWallViewed(this.props.customReturnUrl);
  }

  getUserCreditCode = async () => {
    // Use this function to assign credit to a user
    // on sign up
    return undefined;
  };

  configureLock = async (containerRef) => {
    const loggedOutUserData = this.fetchLoggedOutUserData();
    const { query, hash } = this.props.location;
    // auth lock doesn't pass hash params, so change it to query
    const callbackQuery = hash ? `?${hash.slice(1)}` : '';
    const returnUrl = this.props.customReturnUrl || (query && query.returnUrl);
    const queryWith = query && query.with;

    let allowedConnections = loggedOutUserData.previousService
      ? [loggedOutUserData.previousService]
      : this.props.allowedConnections;

    const forceStrava =
      (returnUrl &&
        (returnUrl.includes('-s-') || returnUrl.includes('invite/strava'))) ||
      queryWith === 'strava';
    if (forceStrava) {
      // Force strava as the connection when specified in a URL
      allowedConnections = [CONNECTIONS.STRAVA];
    }

    const lockConfig = { allowedConnections, callbackQuery };
    const creditCode = await this.getUserCreditCode();

    this.lock = await getLock(
      containerRef,
      false,
      returnUrl,
      lockConfig,
      creditCode,
    );
    this.lock.show();
    this.fetchPreview(returnUrl, lockConfig);

    this.setState({
      displayName: loggedOutUserData.displayName,
      allowedConnections,
      forceStrava,
    });
  };

  fetchLoggedOutUserData = () => {
    const primaryUserId = cookie.load(USER_COOKIE_NAME);
    const displayName = cookie.load(USER_DISPLAY_NAME_COOKIE_NAME);

    let previousService;
    if (primaryUserId) {
      if (
        primaryUserId.includes('strava') &&
        this.props.allowedConnections.includes(CONNECTIONS.STRAVA)
      ) {
        previousService = CONNECTIONS.STRAVA;
      } else if (
        primaryUserId.startsWith('google') &&
        this.props.allowedConnections.includes(CONNECTIONS.GOOGLE)
      ) {
        previousService = CONNECTIONS.GOOGLE;
      } else if (
        primaryUserId.startsWith('facebook') &&
        this.props.allowedConnections.includes(CONNECTIONS.FACEBOOK)
      ) {
        previousService = CONNECTIONS.FACEBOOK;
      }
    }

    return { displayName, primaryUserId, previousService };
  };

  handleNotYou = () => {
    this.reconfigureLock();
  };

  handleMoreConnectionOptions = () => {
    this.reconfigureLock();
  };

  handlePreviouslyLoggedInWithStrava = () => {
    if (__CLIENT__) {
      window.location.href = '/login?with=strava';
    }
  };

  reconfigureLock = () => {
    this.setState({ showSecondLock: true });
    this.props.authService.unknownUserIsNotPreviousLoggedInUser();
    this.configureLock(secondLockContainerId);
  };

  fetchPreview = (returnUrl = '', lockConfig = {}) => {
    if (!this.props.fetchPreview) return;
    const params = returnUrl.split('/');
    // Preview an event, e.g. event review or save
    if (params[1] === 'e' || params[1] === 'save-event') {
      const allowedConnections = lockConfig.allowedConnections || [];
      /* eslint-disable prettier/prettier */
      this.setState(
        allowedConnections.length === 1 && allowedConnections[0] === 'strava'
          ? {
              heading: 'Let’s get you signed in',
              blurb: 'To proceed, sign in with your Strava account below.',
              stravaOnly: true,
            }
          : {
              heading: 'Sign in to leave a review',
              blurb: 'To leave a review you must be signed in to Let’s Do This',
            },
      );
      /* eslint-enable prettier/prettier */

      // Set copy for saves only
      if (params[1] === 'save-event') {
        this.setState({
          heading: 'Sign in to save an event',
          blurb: 'To save events you must be signed in to Let’s Do This',
        });
      }
    }
  };

  render() {
    const {
      heading,
      blurb,
      displayName,
      forceStrava,
      showSecondLock,
    } = this.state;
    const { transparentLayout, event } = this.props;
    const singleConnection =
      this.state.allowedConnections &&
      this.state.allowedConnections.length === 1;

    return (
      <div
        className={[transparentLayout ? layoutTransparentStyle : layout].join(
          ' ',
        )}
      >
        <div
          className={[
            transparentLayout ? containerTransparentStyle : container,
          ]}
        >
          {this.props.renderLogo() || (
            <Logo className={logo} inverse={!!event} />
          )}
          {event && (
            <div>
              <header
                className={reviewHeader}
                style={{
                  backgroundImage: `url(${event.images &&
                    event.images.hero.large})`,
                }}
              >
                {event?.images?.logo?.thumbnail && (
                  <EventLogo
                    logo={event.images.logo.thumbnail}
                    className={reviewLogo}
                  />
                )}
                <h2 className={reviewTitle}>{event.title}</h2>
                {event.organiser && (
                  <Link
                    to={event.organiser.permalink}
                    className={reviewOrganiser}
                  >
                    By {event.organiser.title}
                  </Link>
                )}
              </header>
              {displayName && (
                <div>
                  <h1 className={personalisedHeadingStyle}>
                    Welcome back {displayName}!
                  </h1>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a href="#" onClick={this.handleNotYou}>
                    (not you?)
                  </a>
                </div>
              )}
              {!displayName && <h1 className={headingStyle}>{heading}</h1>}
              <p className={this.state.stravaOnly ? shortBlurb : blurbStyle}>
                {blurb}
              </p>
            </div>
          )}
          {!event && (
            <div>
              {heading && <h1 className={headingStyle}>{heading}</h1>}
              {blurb && (
                <p className={this.state.stravaOnly ? shortBlurb : blurbStyle}>
                  {blurb}
                </p>
              )}
            </div>
          )}
          {!showSecondLock && (
            <div
              id={lockContainerId}
              className={[
                singleConnection ? 'shortContainer' : null,
                this.props.lockExtraStyles,
              ].join(' ')}
            />
          )}
          <div
            id={secondLockContainerId}
            className={[showSecondLock ? null : 'hideLockContainer'].join(' ')}
          />
          {!forceStrava && (
            <p className={stravaUser}>
              Previously logged in with Strava?{' '}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" onClick={this.handlePreviouslyLoggedInWithStrava}>
                Click here.
              </a>
            </p>
          )}
          {singleConnection && !forceStrava && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a href="#" onClick={this.handleMoreConnectionOptions}>
              More Login Options?
            </a>
          )}
          {this.props.renderFooter() || (
            <p className={terms}>
              By creating an account, you accept our{' '}
              <Link to="/terms">terms</Link>.
              <br /> Or <Link to="/">go back</Link> to our home page.
            </p>
          )}
        </div>
      </div>
    );
  }
}

Login.propTypes = {
  event: PropTypes.shape(),
  location: routerContextShape.location.isRequired,
  heading: PropTypes.string,
  blurb: PropTypes.string,
  renderLogo: PropTypes.func,
  customReturnUrl: PropTypes.string,
  transparentLayout: PropTypes.bool,
  renderFooter: PropTypes.func,
  allowedConnections: PropTypes.arrayOf(PropTypes.string),
  fetchPreview: PropTypes.bool,
  lockExtraStyles: PropTypes.string,
};

Login.defaultProps = {
  event: null,
  fetchPreview: true,
  heading: 'Let’s get you signed in',
  blurb:
    'To proceed, you’re going to need to get yourself signed in. Simply select an option below.',
  renderLogo: () => null,
  customReturnUrl: '',
  transparentLayout: false,
  renderFooter: () => null,
  allowedConnections: [CONNECTIONS.GOOGLE, CONNECTIONS.FACEBOOK],
  lockExtraStyles: '',
};

const getEventId = (returnUrl = '') => {
  if (!returnUrl) return;
  const params = returnUrl.split('/');
  if (params.length < 3) return;
  const [pageType] = params;
  if (pageType === 'e') {
    return params[2].split('-').pop(); // Parse from 'slug-id' param
  }
  if (params[2].indexOf('{') === 0) {
    return JSON.parse(params[2]).id;
  }
};

const LoginWithGraphQL = (props) => {
  const { query, customReturnUrl } = props.location;
  const returnUrl = customReturnUrl || (query && query.returnUrl);
  const eventId = getEventId(returnUrl);
  if (!eventId) {
    return <Login {...props} />;
  }
  return (
    <Query query={EventSummary} variables={{ eventId }}>
      {({ data }) => {
        return (
          <Login {...props} event={data && data.event ? data.event : null} />
        );
      }}
    </Query>
  );
};

export default withRouter(WithAuthService(LoginWithGraphQL));
